import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
} from '@mui/material';
import type { UserProfile, UserAutodeployConfig } from '@finvari/deliver';
import { setUserAutodeployConfig } from '../firebase';
import { usePools } from '../contexts/PoolsContext';
import { useConfig } from '../contexts/ConfigContext';

interface UserAutodeployDialogProps {
  open: boolean;
  onClose: () => void;
  userProfile: UserProfile;
}

// Configure the user's personal autodeploy: one pool + match criteria. Deliberately separate from the
// Edit Profile dialog (which must NOT gain these fields).
const UserAutodeployDialog: React.FC<UserAutodeployDialogProps> = ({ open, onClose, userProfile }) => {
  const { pools } = usePools();
  const { config: appConfig } = useConfig();
  const config = userProfile.autoDeployConfig;

  const [poolId, setPoolId] = useState(config?.poolId || '');
  const [branchIncludeRegex, setBranchIncludeRegex] = useState(config?.branchIncludeRegex || '');
  const [branchExcludeRegex, setBranchExcludeRegex] = useState(config?.branchExcludeRegex || '');
  const [tagIncludeRegex, setTagIncludeRegex] = useState(config?.tagIncludeRegex || '');
  const [tagExcludeRegex, setTagExcludeRegex] = useState(config?.tagExcludeRegex || '');
  const [autoDeployDrafts, setAutoDeployDrafts] = useState(config?.autoDeployDrafts === true);
  // "Wipe environment" defaults on: absent ⇒ true.
  const [wipeEnvironment, setWipeEnvironment] = useState(config?.wipeEnvironment !== false);
  const [preloadProgramGroupId, setPreloadProgramGroupId] = useState(config?.preloadProgramGroupId || '');
  const [autoRunDefaultTests, setAutoRunDefaultTests] = useState(config?.autoRunDefaultTests === true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const c = userProfile.autoDeployConfig;
      setPoolId(c?.poolId || '');
      setBranchIncludeRegex(c?.branchIncludeRegex || '');
      setBranchExcludeRegex(c?.branchExcludeRegex || '');
      setTagIncludeRegex(c?.tagIncludeRegex || '');
      setTagExcludeRegex(c?.tagExcludeRegex || '');
      setAutoDeployDrafts(c?.autoDeployDrafts === true);
      setWipeEnvironment(c?.wipeEnvironment !== false);
      setPreloadProgramGroupId(c?.preloadProgramGroupId || '');
      setAutoRunDefaultTests(c?.autoRunDefaultTests === true);
      setError(null);
    }
  }, [open, userProfile]);

  const isConfigured = !!config?.poolId;
  // Autodeploy attributes a PR to a user by matching the PR's GitHub author to their linked GitHub
  // username. Without it, none of their PRs can ever match — warn so they set it in Edit Profile.
  const hasGithubUsername = !!userProfile.githubUsername;

  const handleSave = async () => {
    if (!poolId) {
      setError('Select a pool to autodeploy matching PRs to.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const next: UserAutodeployConfig = { poolId };
      if (branchIncludeRegex) next.branchIncludeRegex = branchIncludeRegex;
      if (branchExcludeRegex) next.branchExcludeRegex = branchExcludeRegex;
      if (tagIncludeRegex) next.tagIncludeRegex = tagIncludeRegex;
      if (tagExcludeRegex) next.tagExcludeRegex = tagExcludeRegex;
      next.autoDeployDrafts = autoDeployDrafts;
      next.wipeEnvironment = wipeEnvironment;
      if (preloadProgramGroupId) next.preloadProgramGroupId = preloadProgramGroupId;
      next.autoRunDefaultTests = autoRunDefaultTests;
      await setUserAutodeployConfig(next);
      onClose();
    } catch (err: any) {
      // The callable rejects invalid regexes and unknown pools with invalid-argument; surface its
      // message so the user can fix the offending field.
      console.error('Error saving autodeploy config:', err);
      setError(err?.message || 'Failed to save autodeploy configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleTurnOff = async () => {
    setSaving(true);
    setError(null);
    try {
      await setUserAutodeployConfig(null);
      onClose();
    } catch (err: any) {
      console.error('Error clearing autodeploy config:', err);
      setError(err?.message || 'Failed to turn off autodeploy.');
    } finally {
      setSaving(false);
    }
  };

  const sortedPools = [...pools].sort((a, b) => a.name.localeCompare(b.name));
  // Only predefined program groups are offered (no "Custom" free-text list, unlike the deploy dialog).
  const programGroups = Object.entries(appConfig?.programGroups ?? {})
    .map(([id, group]) => ({ id, ...group }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Configure Autodeploy</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!hasGithubUsername && (
            <Alert severity="warning">
              Set your GitHub username in Edit Profile — autodeploy matches your PRs by their GitHub
              author, so none of your PRs will autodeploy until it's linked.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            Matching PRs of yours are automatically deployed to the selected pool, on your behalf.
            Leave a regex blank to ignore it; with no branch or tag inclusion, nothing matches.
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="autodeploy-pool-label">Pool</InputLabel>
            <Select
              labelId="autodeploy-pool-label"
              label="Pool"
              value={poolId}
              onChange={(e) => setPoolId(e.target.value)}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {sortedPools.map((pool) => (
                <MenuItem key={pool.poolId} value={pool.poolId}>{pool.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Branch include regex"
            value={branchIncludeRegex}
            onChange={(e) => setBranchIncludeRegex(e.target.value)}
            fullWidth
          />
          <TextField
            label="Branch exclude regex"
            value={branchExcludeRegex}
            onChange={(e) => setBranchExcludeRegex(e.target.value)}
            fullWidth
          />
          <TextField
            label="Tag include regex"
            value={tagIncludeRegex}
            onChange={(e) => setTagIncludeRegex(e.target.value)}
            fullWidth
            helperText="Matched against the PR's GitHub label names."
          />
          <TextField
            label="Tag exclude regex"
            value={tagExcludeRegex}
            onChange={(e) => setTagExcludeRegex(e.target.value)}
            fullWidth
          />
          <FormControlLabel
            control={<Switch checked={autoDeployDrafts} onChange={(e) => setAutoDeployDrafts(e.target.checked)} />}
            label="Autodeploy draft PRs"
          />

          <Divider />
          <Typography variant="subtitle2" color="text.secondary">
            Environment Setup
          </Typography>
          <FormControlLabel
            control={<Switch checked={wipeEnvironment} onChange={(e) => setWipeEnvironment(e.target.checked)} />}
            label="Wipe environment"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Wipes the leased environment for a clean start on the first deploy (if the pool allows resets).
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="autodeploy-program-group-label">Preload program group</InputLabel>
            <Select
              labelId="autodeploy-program-group-label"
              label="Preload program group"
              value={preloadProgramGroupId}
              onChange={(e) => setPreloadProgramGroupId(e.target.value)}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {programGroups.map((group) => (
                <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={autoRunDefaultTests} onChange={(e) => setAutoRunDefaultTests(e.target.checked)} />}
            label="Auto-run default tests"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Runs the default tests after each autodeploy (same as the deploy dialog).
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        {isConfigured && (
          <Button onClick={handleTurnOff} disabled={saving} color="error" sx={{ mr: 'auto' }}>
            Turn Off
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserAutodeployDialog;
